/**
 * Navigate to per-module simulation review with a contextual returnTo URL.
 *
 * @module useNavigateToModuleReview
 */

import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '@/lib/routeConstants';

type NavigateToModuleReviewOptions =
  | { readonly returnBasePath: typeof ROUTES.RESULTS; readonly mode: 'results' }
  | { readonly returnBasePath: typeof ROUTES.DASHBOARD; readonly mode: 'dashboard' };

export function useNavigateToModuleReview(options: NavigateToModuleReviewOptions) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return useCallback(
    (resultId: string, moduleName: string) => {
      let returnTo: string;

      if (options.mode === 'results') {
        const returnParams = new URLSearchParams(searchParams);
        returnParams.set('session', resultId);
        returnParams.set('module', moduleName);
        const returnQuery = returnParams.toString();
        returnTo = `${options.returnBasePath}${returnQuery ? `?${returnQuery}` : ''}`;
      } else {
        returnTo = `${options.returnBasePath}?module=${encodeURIComponent(moduleName)}`;
      }

      const reviewPath = `/results/${resultId}/${moduleName}`;
      const reviewQuery = `returnTo=${encodeURIComponent(returnTo)}`;
      navigate(`${reviewPath}?${reviewQuery}`);
    },
    [navigate, options.returnBasePath, options.mode, searchParams],
  );
}
